import React, { useEffect, useRef, useState } from 'react';
// Deep import, not the `src/components` barrel: that pulls in every
// component (and an ESM-only dep via MetadataForm), which jest cannot parse.
import Loader from 'src/components/Loader';
import styles from './CompareSlider.module.scss';

export type CompareSliderProps = {
  /** The "before" image. Until it resolves, the stage shows a spinner. */
  originalUrl: string;
  /** The "after" image. Absent = no comparison yet, just the original. */
  resultUrl?: string | null;
  /** Alt text for both images (the asset name). */
  alt: string;
  /** Covers the stage with a spinner + label while an operation runs. */
  busy?: boolean;
  busyLabel?: string;
  beforeLabel: string;
  afterLabel: string;
};

const CENTRE = 50;

/**
 * The before/after stage: one image, or two with a draggable divider.
 *
 * Extracted from AIImageEditDialog so the cut-out panel gets the same
 * surface. Both panels want the identical behaviour — a single stage used
 * for both states so the image keeps its exact size and position before and
 * after applying, rather than jumping when a result arrives.
 *
 * The divider position lives here rather than in the parent: it is presentation
 * state, and it resets itself whenever a new result arrives (which is what the
 * parent used to do by hand after staging a preview).
 */
export const CompareSlider: React.FC<CompareSliderProps> = ({
  originalUrl,
  resultUrl,
  alt,
  busy,
  busyLabel,
  beforeLabel,
  afterLabel,
}) => {
  const [sliderPos, setSliderPos] = useState(CENTRE);
  const stageRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  // A new result is a new comparison, so start from the middle again.
  useEffect(() => {
    setSliderPos(CENTRE);
  }, [resultUrl]);

  const updateFromClientX = (clientX: number) => {
    const el = stageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setSliderPos(Math.max(0, Math.min(100, pct)));
  };

  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    draggingRef.current = true;
    const clientX = 'touches' in e ? e.touches[0]?.clientX : (e as React.MouseEvent).clientX;
    if (typeof clientX === 'number') updateFromClientX(clientX);
  };

  // Window-level listeners so dragging keeps working when the pointer
  // leaves the slider bounds.
  useEffect(() => {
    const move = (e: MouseEvent | TouchEvent) => {
      if (!draggingRef.current) return;
      const clientX = 'touches' in e ? e.touches[0]?.clientX : (e as MouseEvent).clientX;
      if (typeof clientX === 'number') updateFromClientX(clientX);
    };
    const up = () => {
      draggingRef.current = false;
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
  }, []);

  return (
    <div className={styles.stage}>
      {originalUrl ? (
        <div
          className={resultUrl ? styles.compare : `${styles.compare} ${styles.compareStatic}`}
          ref={stageRef}
          onMouseDown={resultUrl ? handlePointerDown : undefined}
          onTouchStart={resultUrl ? handlePointerDown : undefined}
        >
          {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
          <img className={styles.compareImage} src={originalUrl} alt={alt} draggable={false} />
          {resultUrl && (
            <>
              <div className={styles.compareAfter} style={{ clipPath: `inset(0 0 0 ${sliderPos}%)` }}>
                {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                <img className={styles.compareImage} src={resultUrl} alt={alt} draggable={false} />
              </div>
              <span className={`${styles.compareLabel} ${styles.compareLabelBefore}`}>{beforeLabel}</span>
              <span className={`${styles.compareLabel} ${styles.compareLabelAfter}`}>{afterLabel}</span>
              <div className={styles.divider} style={{ left: `${sliderPos}%` }}>
                <span className={styles.dividerHandle} aria-hidden="true" />
              </div>
            </>
          )}
        </div>
      ) : (
        <div className={styles.placeholder}>
          <Loader color="#fff" />
        </div>
      )}
      {busy && (
        <div className={styles.busy}>
          <Loader color="#fff" />
          {busyLabel && <span>{busyLabel}</span>}
        </div>
      )}
    </div>
  );
};

export default CompareSlider;
